import {
  suggestionHasCritiqueCarouselMarkers,
  suggestionLooksLikeEditDirectiveCluster,
} from "./writeCarouselDropInClassifier";
import {
  filterDropInCarouselSuggestions,
  sanitizeWriteCarouselSuggestionBody,
} from "./writeCarouselSuggestionSanitize";

/** Tools allowed to emit RewriteSuggestionCarousel UI. */
export const WRITE_REWRITE_CAROUSEL_TOOL_NAMES = [
  "present_rewrite_suggestions",
  "generate_write_button_prose",
  "manage_project_documents",
] as const;

export type WriteRewriteCarouselToolName = (typeof WRITE_REWRITE_CAROUSEL_TOOL_NAMES)[number];

const WRITE_REWRITE_CAROUSEL_TOOL_SET = new Set<string>(WRITE_REWRITE_CAROUSEL_TOOL_NAMES);

export function isWriteRewriteCarouselTool(toolName: unknown): boolean {
  return WRITE_REWRITE_CAROUSEL_TOOL_SET.has(String(toolName ?? "").trim());
}

export type CarouselPresentation = {
  intro: string;
  suggestions: string[];
  renderAsCarousel: boolean;
  ui?: {
    type: "RewriteSuggestionCarousel";
    props: {
      intro: string;
      suggestions: string[];
      renderAsCarousel: true;
    };
  };
  /** Machine-facing note when carousel chrome is withheld. */
  skipReason?: string;
};

/**
 * Resolve whether structured suggestions may render as Write Cards.
 *
 * Keep drop-in manuscript replacements; withhold chrome for critique/advice.
 * Do not run a second hard classifier pass after the advice filter — that was
 * rejecting valid rewrite prose that already looked like manuscript text.
 */
export function resolveCarouselPresentation(params: {
  suggestions: string[];
  intro?: string;
  sourcePassage?: string;
}): CarouselPresentation {
  const intro = String(params.intro ?? "").trim();
  const sanitized = params.suggestions
    .map((entry) => sanitizeWriteCarouselSuggestionBody(entry))
    .filter((entry) => entry.trim().length > 0);

  const dropInOnly = filterDropInCarouselSuggestions(sanitized, params.sourcePassage).filter(
    (entry) =>
      !suggestionHasCritiqueCarouselMarkers(entry)
      && !suggestionLooksLikeEditDirectiveCluster(entry),
  );

  if (dropInOnly.length === 0) {
    return {
      intro,
      suggestions: sanitized,
      renderAsCarousel: false,
      skipReason:
        "Suggestions are editorial notes/critique, not drop-in manuscript replacements. Deliver that analysis in plain chat text instead of the rewrite carousel.",
    };
  }

  return {
    intro,
    suggestions: dropInOnly,
    renderAsCarousel: true,
    ui: {
      type: "RewriteSuggestionCarousel",
      props: {
        intro,
        suggestions: dropInOnly,
        renderAsCarousel: true,
      },
    },
  };
}

/** True when a persisted tool result is allowed to paint Write Cards. */
export function toolResultAllowsCarouselRender(params: {
  toolName?: unknown;
  result?: unknown;
}): boolean {
  if (!isWriteRewriteCarouselTool(params.toolName)) return false;
  const result = params.result;
  if (!result || typeof result !== "object") return false;

  const renderFlag = (result as { renderAsCarousel?: unknown }).renderAsCarousel;
  if (renderFlag === false) return false;

  const ui = (result as { ui?: { type?: unknown; props?: { renderAsCarousel?: unknown; suggestions?: unknown } } }).ui;
  if (ui?.type === "RewriteSuggestionCarousel") {
    if (ui.props?.renderAsCarousel === false) return false;
    const suggestions = Array.isArray(ui.props?.suggestions) ? ui.props.suggestions : [];
    const bodies = suggestions.filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0);
    if (bodies.length === 0) return false;
    // Explicit new-path flag: trust server-validated carousel UI.
    if (renderFlag === true || ui.props?.renderAsCarousel === true) return true;
    // Legacy UI payloads: require bodies to survive drop-in advice filtering.
    return filterDropInCarouselSuggestions(bodies).length > 0;
  }

  const suggestions = (result as { suggestions?: unknown }).suggestions;
  if (!Array.isArray(suggestions)) return false;
  const bodies = suggestions.filter(
    (entry): entry is string => typeof entry === "string" && entry.trim().length > 0,
  );
  if (bodies.length === 0) return false;

  if (renderFlag === true) return true;

  // Legacy suggestions-only write-tool results (pre-renderAsCarousel).
  return filterDropInCarouselSuggestions(bodies).length > 0;
}
